import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Download,
  Printer,
  ArrowLeft,
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  Building2,
  Mail,
  Phone,
  FileText,
  CreditCard,
  Timer,
} from "lucide-react";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import Footer from "../../components/landing/Footer";

const RegistrationReceipt = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get registration from location state or fetch by slug
  useEffect(() => {
    const fetchReceipt = async () => {
      setLoading(true);
      try {
        // First check if registration data is passed via location state
        if (location.state?.registration) {
          setRegistration(location.state.registration);
          setLoading(false);
          return;
        }

        // Otherwise, fetch by slug
        if (slug) {
          const response =
            await conferenceAndTradeMissionService.getAllRegisteredEvents(
              "trade_mission"
            );

          if (response.data.status_code === 200) {
            const registrations = response.data.data || [];
            const foundRegistration = registrations.find(
              (reg) => reg.slug === slug
            );

            if (foundRegistration) {
              setRegistration(foundRegistration);
            } else {
              toast.error("Registration not found");
              navigate("/my-registrations");
            }
          } else {
            toast.error("Failed to fetch registration details");
            navigate("/my-registrations");
          }
        } else {
          toast.error("Invalid registration");
          navigate("/my-registrations");
        }
      } catch (error) {
        toast.error("Failed to load receipt");
        navigate("/my-registrations");
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [slug, location.state, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const status = (paymentStatus || "pending").toLowerCase();

    if (status === "paid" || status === "completed" || status === "verified") {
      return {
        label: "Paid",
        icon: CheckCircle2,
        color: "green",
      };
    } else if (status === "pending") {
      return {
        label: "Pending Payment",
        icon: Timer,
        color: "amber",
      };
    } else {
      return {
        label: "Payment Pending",
        icon: CreditCard,
        color: "gray",
      };
    }
  };

  const getEventStatusBadge = (eventStatus) => {
    const status = (eventStatus || "pending").toLowerCase();

    if (status === "approved") {
      return {
        label: "Approved",
        icon: CheckCircle2,
        color: "green",
      };
    } else if (status === "rejected") {
      return {
        label: "Rejected",
        icon: FileText,
        color: "red",
      };
    } else {
      return {
        label: "Pending Review",
        icon: Timer,
        color: "amber",
      };
    }
  };

  const handleDownload = () => {
    window.print();
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-orange-200 animate-spin border-t-orange-500" />
          <div className="flex absolute inset-0 justify-center items-center">
            <FileText size={24} className="text-orange-500" />
          </div>
        </div>
      </div>
    );
  }

  if (!registration) {
    return null;
  }

  const eventData = registration.event || {};
  const paymentStatusConfig = getPaymentStatusBadge(registration.payment_status);
  const eventStatusConfig = getEventStatusBadge(eventData.event_status);
  const PaymentStatusIcon = paymentStatusConfig.icon;
  const EventStatusIcon = eventStatusConfig.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print-only header */}
      <div className="hidden print:block print:mb-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Registration Receipt</h1>
          <p className="text-sm text-gray-600">
            Generated on {new Date().toLocaleString()}
          </p>
        </div>
      </div>

      <div className="px-4 py-8 mt-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          {/* Action buttons - hidden when printing */}
          <div className="flex gap-3 mb-6 print:hidden">
            <motion.button
              onClick={() => navigate(-1)}
              whileHover={{ x: -4 }}
              whileTap={{ scale: 0.95 }}
              className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md"
            >
              <ArrowLeft size={18} />
              Back
            </motion.button>
            <motion.button
              onClick={handleDownload}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-md transition-all hover:from-orange-600 hover:to-orange-700"
            >
              <Download size={18} />
              Download PDF
            </motion.button>
            <motion.button
              onClick={handlePrint}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md"
            >
              <Printer size={18} />
              Print
            </motion.button>
          </div>

          {/* Receipt Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="receipt-content bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden print:shadow-none print:border-0"
          >
            {/* Header */}
            <div className="px-6 py-6 bg-gradient-to-r from-orange-500 to-orange-600 print:bg-orange-600">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="mb-2 text-2xl font-bold text-white">
                    Registration Receipt
                  </h1>
                  <p className="text-orange-100">
                    {eventData.event_title || "Event Registration"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-orange-100">Receipt ID</p>
                  <p className="font-mono text-sm font-bold text-white">
                    {registration.slug?.substring(0, 8).toUpperCase() || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Status Badges */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 print:bg-white">
              <div className="flex flex-wrap gap-3">
                <div
                  className={`flex gap-2 items-center px-3 py-1.5 rounded-lg ${
                    paymentStatusConfig.color === "green"
                      ? "bg-green-100 text-green-700"
                      : paymentStatusConfig.color === "amber"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  <PaymentStatusIcon size={16} />
                  <span className="text-sm font-semibold">
                    {paymentStatusConfig.label}
                  </span>
                </div>
                <div
                  className={`flex gap-2 items-center px-3 py-1.5 rounded-lg ${
                    eventStatusConfig.color === "green"
                      ? "bg-green-100 text-green-700"
                      : eventStatusConfig.color === "red"
                      ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  <EventStatusIcon size={16} />
                  <span className="text-sm font-semibold">
                    {eventStatusConfig.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Content - Two Column Layout */}
            <div className="p-6 print:p-4">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 print:grid-cols-2 print:gap-4">
                {/* Left Column */}
                <div className="space-y-4 print:space-y-3">
                  {/* Event Information */}
                  <div>
                    <h2 className="mb-3 text-base font-bold text-gray-900 border-b border-gray-200 pb-1.5 print:text-sm print:mb-2 print:pb-1">
                      Event Information
                    </h2>
                    <div className="space-y-2.5 print:space-y-2">
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                          Event Title
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                          {eventData.event_title || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                          Event Type
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-gray-900 capitalize print:text-xs">
                          {eventData.event_type?.join(", ") || "N/A"}
                        </p>
                      </div>
                      {(eventData.event_start_date || eventData.event_date) && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            Start Date
                          </p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                            {formatDate(eventData.event_start_date || eventData.event_date)}
                          </p>
                        </div>
                      )}
                      {(eventData.event_end_date || eventData.event_date) && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            End Date
                          </p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                            {formatDate(eventData.event_end_date || eventData.event_date)}
                          </p>
                        </div>
                      )}
                      {(eventData.event_start_time || eventData.event_time) && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            Start Time
                          </p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                            {formatTime(eventData.event_start_time || eventData.event_time)}
                          </p>
                        </div>
                      )}
                      {(eventData.event_end_time || eventData.event_time) && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            End Time
                          </p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                            {formatTime(eventData.event_end_time || eventData.event_time)}
                          </p>
                        </div>
                      )}
                      {eventData.venue_name && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            Venue
                          </p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                            {eventData.venue_name}
                          </p>
                          {eventData.venue_street_address && (
                            <p className="mt-0.5 text-xs text-gray-600 print:text-[10px]">
                              {eventData.venue_street_address}
                            </p>
                          )}
                          <p className="mt-0.5 text-xs text-gray-600 print:text-[10px]">
                            {eventData.city}, {eventData.country}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Information */}
                  {registration.company_name && (
                    <div>
                      <h2 className="mb-3 text-base font-bold text-gray-900 border-b border-gray-200 pb-1.5 print:text-sm print:mb-2 print:pb-1">
                        Company Information
                      </h2>
                      <div className="space-y-2.5 print:space-y-2">
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            Company Name
                          </p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                            {registration.company_name}
                          </p>
                        </div>
                        {registration.vat_registration_number && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              VAT Registration Number
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                              {registration.vat_registration_number}
                            </p>
                          </div>
                        )}
                        {registration.business_email && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Business Email
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs break-all">
                              {registration.business_email}
                            </p>
                          </div>
                        )}
                        {(registration.telephone_number ||
                          registration.cell_number) && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Contact Number
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                              {registration.cell_number ||
                                registration.telephone_number ||
                                "N/A"}
                            </p>
                          </div>
                        )}
                        {registration.website_address && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Website
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs break-all">
                              {registration.website_address}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-4 print:space-y-3">
                  {/* Contact Person Information */}
                  {registration.contact_person_name && (
                    <div>
                      <h2 className="mb-3 text-base font-bold text-gray-900 border-b border-gray-200 pb-1.5 print:text-sm print:mb-2 print:pb-1">
                        Contact Person
                      </h2>
                      <div className="space-y-2.5 print:space-y-2">
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            Name
                          </p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                            {registration.contact_person_name}
                          </p>
                        </div>
                        {registration.contact_person_designation && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Designation
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                              {registration.contact_person_designation}
                            </p>
                          </div>
                        )}
                        {registration.contact_person_email && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Email
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs break-all">
                              {registration.contact_person_email}
                            </p>
                          </div>
                        )}
                        {(registration.contact_person_telephone ||
                          registration.contact_person_cell_number) && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Phone
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                              {registration.contact_person_cell_number ||
                                registration.contact_person_telephone ||
                                "N/A"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Applicant Information */}
                  {registration.applicant_name && (
                    <div>
                      <h2 className="mb-3 text-base font-bold text-gray-900 border-b border-gray-200 pb-1.5 print:text-sm print:mb-2 print:pb-1">
                        Applicant Information
                      </h2>
                      <div className="space-y-2.5 print:space-y-2">
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            Applicant Name
                          </p>
                          <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                            {registration.applicant_name}
                          </p>
                        </div>
                        {registration.signatory_designation && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Designation
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                              {registration.signatory_designation}
                            </p>
                          </div>
                        )}
                        {registration.declaration_date && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Declaration Date
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                              {formatDateTime(registration.declaration_date)}
                            </p>
                          </div>
                        )}
                        {registration.agent_code && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                              Agent Code
                            </p>
                            <p className="mt-0.5 text-sm font-semibold text-gray-900 print:text-xs">
                              {registration.agent_code}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Registration Details */}
                  <div>
                    <h2 className="mb-3 text-base font-bold text-gray-900 border-b border-gray-200 pb-1.5 print:text-sm print:mb-2 print:pb-1">
                      Registration Details
                    </h2>
                    <div className="space-y-2.5 print:space-y-2">
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                          Registration ID
                        </p>
                        <p className="mt-0.5 font-mono text-sm font-semibold text-gray-900 print:text-xs">
                          {registration.slug || "N/A"}
                        </p>
                      </div>
                      {registration.application_id && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                            Application ID
                          </p>
                          <p className="mt-0.5 font-mono text-sm font-semibold text-gray-900 print:text-xs">
                            {registration.application_id}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                          Payment Status
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-gray-900 capitalize print:text-xs">
                          {paymentStatusConfig.label}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase print:text-[9px]">
                          Event Status
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-gray-900 capitalize print:text-xs">
                          {eventStatusConfig.label}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-4 mt-4 border-t border-gray-200 print:pt-3 print:mt-3">
                <p className="text-[10px] text-center text-gray-500 print:text-[9px]">
                  This is an official receipt for your event registration. Please
                  keep this document for your records.
                </p>
                <p className="mt-1 text-[10px] text-center text-gray-500 print:text-[9px]">
                  Generated on {new Date().toLocaleString()}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0.5cm;
            /* Remove browser headers and footers */
            margin-top: 0.5cm;
            margin-bottom: 0.5cm;
            margin-left: 0.5cm;
            margin-right: 0.5cm;
          }
          
          /* Hide all navigation, headers, and footers */
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          nav, footer, header, .print\\:hidden {
            display: none !important;
          }
          
          /* Ensure receipt content starts at top */
          .receipt-content {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          
          .print\\:block {
            display: block !important;
          }
          .print\\:bg-white {
            background: white !important;
          }
          .print\\:bg-orange-600 {
            background: #ea580c !important;
          }
          .print\\:border-0 {
            border: 0 !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:mb-6 {
            margin-bottom: 1.5rem !important;
          }
          .print\\:p-4 {
            padding: 1rem !important;
          }
          .print\\:grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .print\\:gap-4 {
            gap: 1rem !important;
          }
          .print\\:space-y-3 {
            --tw-space-y-reverse: 0;
            margin-top: calc(0.75rem * calc(1 - var(--tw-space-y-reverse))) !important;
            margin-bottom: calc(0.75rem * var(--tw-space-y-reverse)) !important;
          }
          .print\\:space-y-2 > * + * {
            margin-top: 0.5rem !important;
          }
          .print\\:text-sm {
            font-size: 0.875rem !important;
            line-height: 1.25rem !important;
          }
          .print\\:text-xs {
            font-size: 0.75rem !important;
            line-height: 1rem !important;
          }
          .print\\:text-\\[10px\\] {
            font-size: 10px !important;
          }
          .print\\:text-\\[9px\\] {
            font-size: 9px !important;
          }
          .print\\:mb-2 {
            margin-bottom: 0.5rem !important;
          }
          .print\\:pb-1 {
            padding-bottom: 0.25rem !important;
          }
          .print\\:pt-3 {
            padding-top: 0.75rem !important;
          }
          .print\\:mt-3 {
            margin-top: 0.75rem !important;
          }
        }
      `}</style>
      
      {/* Print Instructions - Hidden by default, shown as tooltip */}
      <div className="hidden print:hidden">
        <div className="fixed bottom-4 right-4 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-lg max-w-xs">
          <p className="text-xs text-blue-800">
            <strong>Tip:</strong> In the print dialog, uncheck "Headers and footers" to remove the URL from the top of the page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationReceipt;
